import { z } from "zod";
import type { Field, Values } from "./schema";

export const addressParts = [
  { key: "address1", label: "Address 1" },
  { key: "address2", label: "Address 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State / region" },
  { key: "postalCode", label: "Postal code" },
  { key: "country", label: "Country" },
] as const;

export const coordinateParts = [
  { key: "latitude", label: "Latitude" },
  { key: "longitude", label: "Longitude" },
] as const;

export function mapPartValue(
  field: Field,
  values: Values,
  key: string,
  answers: Record<string, unknown> = {},
): string {
  const settings = field.map;
  if (settings?.disabledParts?.includes(key)) return "";
  const linked = settings?.linked?.[key];
  const source = settings?.defaults?.[key] || settings?.mapping?.[key];
  const selected = values[field.id];
  const location =
    selected &&
    typeof selected === "object" &&
    !Array.isArray(selected) &&
    selected.location &&
    typeof selected.location === "object"
      ? (selected.location as Record<string, unknown>)
      : undefined;
  const value = linked
    ? values[linked]
    : settings?.fixed?.[key] !== undefined
      ? settings.fixed[key]
      : answers[key] !== undefined
        ? answers[key]
        : source
          ? values[source]
          : ["latitude", "longitude"].includes(key)
            ? location?.[key]
            : undefined;
  return typeof value === "string" || typeof value === "number"
    ? String(value).trim()
    : "";
}
export function mapDefaultAddress(
  field: Field,
  values: Values,
  answers: Record<string, unknown> = {},
): string {
  return addressParts
    .map(({ key }) => mapPartValue(field, values, key, answers))
    .filter(Boolean)
    .join(", ");
}
/** Blank, incomplete and out-of-range coordinates must never become a map at 0,0. */
export function coordinatesLocation(
  latitude: string,
  longitude: string,
): MapLocation | undefined {
  if (!latitude.trim() || !longitude.trim()) return;
  const lat = Number(latitude),
    lon = Number(longitude);
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lon) ||
    Math.abs(lat) > 85.05112878 ||
    Math.abs(lon) > 180
  )
    return;
  return {
    id: `coordinates:${lat},${lon}`,
    label: `${lat}, ${lon}`,
    latitude: lat,
    longitude: lon,
  };
}

export const mapSettingsSchema = z.object({
  disabledParts: z.array(z.string()).max(50).optional(),
  locationMode: z.enum(["address", "coordinates", "image"]).optional(),
  defaults: z.record(z.string(), z.string()).optional(),
  linked: z.record(z.string(), z.string()).optional(),
  fixed: z.record(z.string(), z.string().max(2000)).optional(),
  mapping: z.record(z.string(), z.string()).optional(),
  width: z.number().int().min(128).max(1280).default(640),
  height: z.number().int().min(128).max(1280).default(360),
  zoom: z.number().int().min(1).max(20).default(14),
  style: z
    .enum(["roadmap", "satellite", "terrain", "hybrid"])
    .default("roadmap"),
});
export type MapSettings = z.infer<typeof mapSettingsSchema>;
export const mapLocationSchema = z.object({
  id: z.string().min(1).max(200),
  label: z.string().min(1).max(500),
  latitude: z.number().finite().min(-85.05112878).max(85.05112878),
  longitude: z.number().finite().min(-180).max(180),
});
export type MapLocation = z.infer<typeof mapLocationSchema>;
export const mapLocationsSchema = z.array(mapLocationSchema).max(20);
export const mapProviderInfoSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9_-]{1,80}$/),
  label: z.string().min(1).max(100),
  notice: z.string().min(1).max(1000),
});
export type MapProviderInfo = z.infer<typeof mapProviderInfoSchema>;
export const mapProvenanceSchema = z.object({
  providerId: mapProviderInfoSchema.shape.id,
  location: mapLocationSchema,
  attribution: z.string().min(1).max(1000),
});
export interface MapRequestScope {
  tenantId: string;
  subject: string;
  signal: AbortSignal;
}
export interface MapProvider {
  info: MapProviderInfo;
  search(query: string, scope: MapRequestScope): Promise<MapLocation[]>;
  render(
    location: MapLocation,
    settings: MapSettings,
    scope: MapRequestScope,
  ): Promise<{ data: Uint8Array; attribution: string }>;
}
export interface MapSearchResult {
  provider: MapProviderInfo;
  locations: MapLocation[];
}
export interface MapSelection extends Record<string, unknown> {
  assetId: string;
  providerId: string;
  location: MapLocation;
  attribution: string;
}
export interface MapAssetInfo {
  id: string;
  name: string;
  width: number;
  height: number;
  bytes: number;
}
export interface MapGenerated {
  asset: MapAssetInfo;
  value: MapSelection;
}
export interface MapFormServices {
  /** Required for direct coordinate generation without a search request. */
  provider?: MapProviderInfo;
  notice: string;
  search(query: string, signal: AbortSignal): Promise<MapSearchResult>;
  create(
    location: MapLocation,
    providerId: string,
    field: Field,
    signal: AbortSignal,
  ): Promise<MapGenerated>;
}
